<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  existingPhotos: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue', 'removeExisting']);

const MAX_PHOTOS = 5;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const { t } = useI18n();

const fileInput = ref(null);
const previewItems = ref([]);

const totalCount = computed(
  () => props.existingPhotos.length + previewItems.value.length
);
const canAddMore = computed(() => totalCount.value < MAX_PHOTOS);
const remainingSlots = computed(() => MAX_PHOTOS - totalCount.value);

const triggerUpload = () => {
  fileInput.value?.click();
};

const onFilesPicked = e => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  const validFiles = files.filter(file => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      useAlert(t('CANNED_MGMT.PHOTOS.INVALID_FILE'));
      return false;
    }
    return true;
  });

  const slotsAvailable = remainingSlots.value;
  if (validFiles.length > slotsAvailable) {
    useAlert(t('CANNED_MGMT.PHOTOS.MAX_LIMIT', { max: MAX_PHOTOS }));
  }

  const filesToAdd = validFiles.slice(0, slotsAvailable);

  filesToAdd.forEach(file => {
    const url = URL.createObjectURL(file);
    previewItems.value.push({ file, url, filename: file.name });
  });

  emit(
    'update:modelValue',
    previewItems.value.map(entry => entry.file)
  );
  e.target.value = '';
};

const removeNewPhoto = index => {
  const removed = previewItems.value[index];
  if (removed?.url) {
    URL.revokeObjectURL(removed.url);
  }
  previewItems.value.splice(index, 1);
  emit(
    'update:modelValue',
    previewItems.value.map(p => p.file)
  );
};

const removeExistingPhoto = photoId => {
  emit('removeExisting', photoId);
};

onUnmounted(() => {
  previewItems.value.forEach(entry => {
    if (entry.url) URL.revokeObjectURL(entry.url);
  });
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp"
      multiple
      class="hidden"
      @change="onFilesPicked"
    />

    <!-- Existing photos from server -->
    <div
      v-if="existingPhotos.length || previewItems.length"
      class="flex flex-wrap gap-2"
    >
      <div
        v-for="photo in existingPhotos"
        :key="'existing-' + photo.id"
        class="relative group size-20 rounded-lg overflow-hidden border border-n-weak"
      >
        <img
          :src="photo.url"
          :alt="photo.filename"
          class="size-full object-cover"
        />
        <button
          type="button"
          class="absolute top-0.5 right-0.5 size-5 flex items-center justify-center rounded-full bg-n-slate-12/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
          :title="$t('CANNED_MGMT.PHOTOS.REMOVE')"
          @click="removeExistingPhoto(photo.id)"
        >
          <span class="i-lucide-x size-3" />
        </button>
      </div>

      <!-- New photos preview -->
      <div
        v-for="(item, index) in previewItems"
        :key="'new-' + index"
        class="relative group size-20 rounded-lg overflow-hidden border border-n-weak"
      >
        <img
          :src="item.url"
          :alt="item.filename"
          class="size-full object-cover"
        />
        <button
          type="button"
          class="absolute top-0.5 right-0.5 size-5 flex items-center justify-center rounded-full bg-n-slate-12/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
          :title="$t('CANNED_MGMT.PHOTOS.REMOVE')"
          @click="removeNewPhoto(index)"
        >
          <span class="i-lucide-x size-3" />
        </button>
      </div>
    </div>

    <!-- Add button -->
    <div v-if="canAddMore" class="flex gap-2">
      <NextButton
        type="button"
        size="sm"
        color-scheme="secondary"
        faded
        icon="i-lucide-image-plus"
        :label="$t('CANNED_MGMT.PHOTOS.UPLOAD')"
        @click="triggerUpload"
      />
      <span class="text-xs text-n-slate-11 self-center">
        {{
          $t('CANNED_MGMT.PHOTOS.COUNT', {
            current: totalCount,
            max: MAX_PHOTOS,
          })
        }}
      </span>
    </div>
  </div>
</template>
